import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { SignOut, MagnifyingGlass, Bell, Plus, CalendarBlank } from 'phosphor-react';
import { useAuthWrapper, useCapabilities, socketWrapper } from '_helpers';
import { cn } from '_ui';
import { visibleNavItems } from './nav-config';

function initials(name = '') {
    return name.trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '·';
}

function NavItem({ item, onClick }) {
    const { pathname } = useLocation();
    const active = pathname === item.to || pathname.startsWith(item.to + '/');
    const Icon = item.icon;
    return (
        <Link to={item.to} onClick={onClick}
            className={cn(
                'group flex w-full items-center gap-3 rounded-[14px] px-4 py-[11px] text-[14.5px] transition-all duration-200',
                active
                    ? 'bg-white/10 text-cream-50'
                    : 'text-cream-50/60 hover:bg-white/[0.07] hover:text-cream-50/90',
            )}>
            <Icon size={18} weight={active ? 'fill' : 'regular'} className="shrink-0" />
            <span className="font-medium">{item.label}</span>
            <span className={cn(
                'ml-auto h-1.5 w-1.5 rounded-full bg-rose transition-opacity duration-200',
                active ? 'opacity-100' : 'opacity-0',
            )} />
        </Link>
    );
}

function UserCard({ user, onLogout }) {
    return (
        <div className="flex items-center gap-3 border-t border-white/10 pt-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brass to-clay text-sm font-bold text-white">
                {initials(user.name)}
            </span>
            <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-medium text-cream-50">{user.name}</p>
                <p className="text-[11px] text-cream-50/50">{user.role === 'manager' ? 'Chủ tiệm' : 'Nhân viên'}</p>
            </div>
            <button onClick={onLogout} aria-label="Đăng xuất"
                className="flex h-8 w-8 items-center justify-center rounded-full text-cream-50/50 transition-colors hover:bg-white/10 hover:text-cream-50">
                <SignOut size={16} />
            </button>
        </div>
    );
}

function TopBar({ caps }) {
    const now = new Date();
    const monthStr = `Tháng ${now.getMonth() + 1}, ${now.getFullYear()}`;
    return (
        <header className="sticky top-0 z-15 hidden border-b border-espresso/[0.08] bg-cream-200/85 backdrop-blur-xl md:block">
            <div className="flex items-center justify-between gap-5 px-8 py-3.5">
                <div className="relative max-w-[340px] flex-1">
                    <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sand-400" />
                    <input
                        className="w-full rounded-[11px] border border-sand-200 bg-cream-50 py-[11px] pl-10 pr-4 text-sm text-espresso outline-none transition-all placeholder:text-sand-400 focus:border-rose focus:ring-[3px] focus:ring-rose-tint"
                        placeholder="Tìm hoá đơn, khách, nhân viên…"
                    />
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-[11px] border border-sand-200 bg-cream-50 px-3.5 py-2 text-[13.5px] text-espresso-soft">
                        <CalendarBlank size={15} />
                        {monthStr}
                    </div>
                    <button className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-sand-200 bg-cream-50 text-espresso-soft transition-colors hover:border-espresso-soft hover:bg-cream hover:text-espresso">
                        <Bell size={17} />
                    </button>
                    {caps.canCreateTransaction && (
                        <Link to="/tao-hoa-don"
                            className="flex items-center gap-2 rounded-xl bg-espresso px-4 py-[11px] text-sm font-semibold text-cream transition-transform hover:-translate-y-px">
                            <Plus size={16} />
                            Tạo hoá đơn
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}

export function AppShell({ children }) {
    const caps = useCapabilities();
    const auth = useAuthWrapper();
    const user = auth.getUserData();
    const items = visibleNavItems(caps);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        socketWrapper.connect();
        return () => socketWrapper.disconnect();
    }, []);

    const groups = items.reduce((acc, it) => {
        (acc[it.group] = acc[it.group] || []).push(it);
        return acc;
    }, {});

    return (
        <div className="min-h-[100dvh] bg-cream-200">
            {/* Desktop dark sidebar */}
            <aside className="fixed inset-y-0 left-0 z-20 hidden w-[248px] flex-col bg-espresso-deep px-4 py-[22px] md:flex">
                <div className="flex items-center gap-3 px-2 pb-[22px]">
                    <img src="/logo-badge.png" alt="Sunny Nail" className="h-[42px] w-[42px] rounded-full bg-cream-50/90 object-contain p-0.5" />
                    <div className="leading-none">
                        <span className="block font-serif text-[19px] text-cream-50">Sunny Nail</span>
                        <span className="mt-1 block text-[8.5px] uppercase tracking-eyebrow text-brass">Quản lý tiệm</span>
                    </div>
                </div>
                <nav className="flex-1 space-y-5 overflow-y-auto">
                    {Object.entries(groups).map(([group, list]) => (
                        <div key={group}>
                            <p className="px-2.5 pb-2 text-[9.5px] uppercase tracking-[0.2em] text-cream-50/40">{group}</p>
                            <div className="space-y-0.5">
                                {list.map(it => <NavItem key={it.to} item={it} />)}
                            </div>
                        </div>
                    ))}
                </nav>
                <UserCard user={user} onLogout={auth.logout} />
            </aside>

            {/* Mobile top bar */}
            <div className="fixed inset-x-0 top-0 z-30 px-4 pt-4 md:hidden">
                <div className="mx-auto flex max-w-md items-center justify-between rounded-full bg-espresso-deep/95 px-5 py-3 shadow-ambient ring-1 ring-white/10 backdrop-blur-xl">
                    <div className="flex items-center gap-2.5">
                        <img src="/logo-badge.png" alt="" className="h-8 w-8 rounded-full bg-cream-50/90 object-contain p-0.5" />
                        <span className="font-serif text-lg text-cream-50">Sunny Nail</span>
                    </div>
                    <button aria-label="Mở menu" onClick={() => setOpen(o => !o)}
                        className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-cream-50 active:scale-95">
                        <span className={cn('absolute h-0.5 w-5 rounded-full bg-cream-50 transition-all duration-500 ease-fluid', open ? 'rotate-45' : '-translate-y-1.5')} />
                        <span className={cn('absolute h-0.5 w-5 rounded-full bg-cream-50 transition-all duration-500 ease-fluid', open ? '-rotate-45' : 'translate-y-1.5')} />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                        className="fixed inset-0 z-20 flex flex-col bg-espresso-deep/95 px-6 pb-10 pt-28 backdrop-blur-2xl md:hidden">
                        <motion.nav
                            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1], delay: 0.08 }}
                            className="flex flex-1 flex-col gap-1">
                            {items.map(it => <NavItem key={it.to} item={it} onClick={() => setOpen(false)} />)}
                        </motion.nav>
                        <UserCard user={user} onLogout={auth.logout} />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Content */}
            <div className="md:ml-[248px]">
                <TopBar caps={caps} />
                <main className="px-4 pb-12 pt-20 md:px-8 md:pt-8">
                    {children}
                </main>
            </div>
        </div>
    );
}
